import {
  Button,
  Card,
  Divider,
  Group,
  Modal,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconPlus } from '@tabler/icons-react';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import InvoiceApproverAdapter from '../adapters/invoiceApproversAdapter';
import InvoiceApproverForm from '../components/InvoiceApproverForm';
import InvoiceApproversList from '../components/InvoiceApproversList';
import useFetchInvoiceApprovers from '../hooks/useFetchInvoiceApprovers';

function InvoiceApprovers() {
  const [
    approvers = [],
    error,
    isLoading,
    invalidateApproversCache,
    refetchApprovers,
  ] = useFetchInvoiceApprovers();
  const [localApprovers, setLocalApprovers] = useState(approvers);
  const [modalOpened, { open: openModal, close: closeModal }] =
    useDisclosure(false);

  useEffect(() => {
    if (approvers?.length) {
      setLocalApprovers(approvers);
    }
  }, [approvers]);

  const handleFormSuccess = useCallback(() => {
    toast.success('Invoice Approver details saved successfully.');
    refetchApprovers();
    closeModal();
  }, [closeModal]);

  const handleFormError = useCallback(() => {
    toast.error('Something went wrong. Please try again.');
  }, []);

  const handleApproverEdit = useCallback(
    async ({ value, columnId, data }) => {
      const updatedApproverDetails = {
        ...data,
        [columnId]: value,
      };

      const updatedApprovers = localApprovers.map((approver) => {
        if (approver._id === data._id) {
          return { ...updatedApproverDetails };
        }
        return approver;
      });
      setLocalApprovers(updatedApprovers);

      try {
        await InvoiceApproverAdapter.setInvoiceApprover(updatedApproverDetails);
      } catch (err) {
        console.error(
          '🚩 ~ Failed to update approver details ~ err:',
          err.message,
        );
        toast.error('Failed to update approver details.');
      } finally {
        refetchApprovers();
      }
    },
    [localApprovers],
  );

  return (
    <>
      {/* Invoice Approvers Section */}
      <Card>
        <Group position="apart">
          <Stack spacing="none">
            <Title order={3}>Invoice Approvers</Title>
            <Text size="sm" color="dimmed">
              Manage service invoice approvers and their approval limits.
            </Text>
          </Stack>

          {/* Add or Update Invoice Approver */}
          <Button
            type="button"
            onClick={openModal}
            leftIcon={<IconPlus size={14}></IconPlus>}>
            ADD APPROVER
          </Button>
        </Group>
        <Divider my="sm" />

        {/* Invoice Approver's List */}
        <InvoiceApproversList
          approvers={localApprovers}
          error={error}
          loading={isLoading}
          onEdit={handleApproverEdit}
        />
      </Card>

      {/* Add / Edit Invoice Approver Modal */}
      <Modal
        size="md"
        opened={modalOpened}
        onClose={closeModal}
        title="New Approver"
        closeOnClickOutside={false}
        centered>
        <InvoiceApproverForm
          onSuccess={handleFormSuccess}
          onError={handleFormError}></InvoiceApproverForm>
      </Modal>
    </>
  );
}

export default InvoiceApprovers;
